import {
  AvatarGroup,
  Box,
  ClickAwayListener,
  Popover,
  Popper,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar from '../exo/ExoAvatar';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { ExoVariants } from '../exo/ExoVariants';
import { NewPersonLead } from '../new/NewPersonLead';
import { NewCompanyLead } from '../new/NewCompanyLead';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import Cookies from 'universal-cookie';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';
import ExoDrawer from '../exo/ExoDrawer';

const ECFromStack = ({
  from,
  subject,
  avatarSize = '30px',
  cc,
  showEmail,
  openFromMobile = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();
  const cookies = new Cookies();
  const navigate = useNavigate();

  // add to leads
  const [open, setOpen] = useState(false);
  const leadsCompaniesRef = useRef(null);
  const leadsPersonsRef = useRef(null);
  const [variants, setVariants] = useState([]);

  //navigate to the selectedSender (lead)
  const [selectedSender, setSelectedSender] = useState(null);

  // Show Participants popper

  const [participantsAnchorEl, setParticipantsAnchorEl] = useState(null);
  const [participantsSource, setParticipantsSource] = useState('from'); // 'to' or 'cc'
  function handleOpenNewParticipantsPopper(e, source = 'from') {
    setParticipantsAnchorEl(e.currentTarget);
    setParticipantsSource(source);
  }

  useEffect(() => {
    if (!selectedSender) return;

    setVariants([
      {
        label: t('Lead Company'),
        Icon: AddBusinessIcon,
        content: (
          <NewCompanyLead
            onClose={() => setOpen(false)}
            onCreate={(data) => handleChange(leadsCompaniesRef, data)}
            onUpdate={(data) => handleChange(leadsCompaniesRef, data)}
            startData={{
              responsibleUser: {
                id: cookies.get('userId'),
              },
              emails: [
                {
                  emailAddress: selectedSender.email,
                  isInvoiceMail: true,
                  isPrimary: true,
                },
              ],
            }}
          />
        ),
      },
      {
        label: t('Person'),
        Icon: PersonAddAlt1Icon,
        content: (
          <NewPersonLead
            onClose={() => setOpen(false)}
            onCreate={(data) => handleChange(leadsPersonsRef, data)}
            onUpdate={(data) => handleChange(leadsPersonsRef, data)}
            startData={{
              responsibleUser: {
                id: cookies.get('userId'),
              },
              emails: [
                {
                  emailAddress: selectedSender.email,
                  isInvoiceMail: true,
                  isPrimary: true,
                },
              ],
            }}
          />
        ),
      },
    ]);
  }, [selectedSender, t]);

  function handleChange(ref, data) {
    if (!ref) return;
    ref.current.addRow(data);
  }

  function getFormattedData(participant, type) {
    const id = participant?.id;

    const typeMap = {
      companyLead: {
        label: `${participant?.nameShort || participant?.name}`,
        to: `/leads/companies/${id}`,
        picture: participant?.logo?.temporaryUrl,
      },
      company: {
        label: `${participant?.nameShort || participant?.name}`,
        to: `/companies/${id}`,
        picture: participant?.logo?.temporaryUrl,
      },
      personLead: {
        label: `${participant?.firstName} ${participant?.lastName}`,
        to: `/leads/persons/${id}`,
      },
      contact: {
        label: `${participant?.firstName} ${participant?.lastName}`,
        to: `/contacts/${id}`,
      },
      user: {
        label: `${participant?.firstName} ${participant?.lastName}`,
        to: `/employees/${participant?.id}`,
        picture: participant?.picture?.temporaryUrl,
      },
    };
    if (typeMap[type] === undefined) {
      return {
        label: participant?.name || participant?.email,
        isUnknown: true,
      };
    }
    return typeMap[type];
  }

  function getLabel(array) {
    let label = '';
    array.forEach((element, index) => {
      const data = getFormattedData(element.participant, element.type);
      label += data?.label;
      if (index < array.length - 1) label += ', ';
    });
    return label;
  }

  function getEmail(array) {
    var label = '';
    var collection = [];
    array.forEach((element, index) => {
      if (!collection.includes(element.email)) {
        collection.push(element.email);
        label += `<${element.email}> `;
      }
    });

    return label;
  }

  // mobile view
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 1200);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 800);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const Container = isSmallScreen ? Popover : Popper;

  return (
    <Box className="w-full flex items-start gap-2">
      <AvatarGroup
        total={from.length}
        size={avatarSize}
        max={2}
        sx={{
          '.MuiAvatar-root': { border: '1px solid transparent!important' },
          '.MuiAvatarGroup-avatar': {
            width: avatarSize,
            height: avatarSize,
            fontSize: '14px',
            fontWeight: 500,
            cursor: 'pointer',
          },
        }}
        onClick={
          isSmallScreen && !openFromMobile
            ? undefined
            : (e) => handleOpenNewParticipantsPopper(e, 'from')
        }
      >
        {from?.map((element, index) => {
          const data = getFormattedData(element.participant, element.type);

          return (
            <ExoAvatar
              picture={data?.picture}
              type={'contact'}
              name={data?.label}
              size={avatarSize}
              icon={data?.isUnknown ? <AddIcon fontSize="small" /> : undefined}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  borderColor: colors.info,
                },
              }}
              onClick={(e) => {
                if (from.length === 1) {
                  const isUnknown = getFormattedData(
                    from[0]?.participant,
                    from[0]?.type
                  )?.isUnknown;

                  if (isUnknown) {
                    setSelectedSender(from[0]);
                    setOpen(true);
                  } else if (!isSmallScreen || openFromMobile) {
                    handleOpenNewParticipantsPopper(e, 'from');
                  }
                } else if (!isSmallScreen || openFromMobile) {
                  handleOpenNewParticipantsPopper(e, 'from');
                }
              }}
            />
          );
        })}
      </AvatarGroup>
      <Box className="flex flex-col justify-start items-start w-full overflow-hidden">
        <Typography
          fontWeight={600}
          textAlign="start"
          className="w-full text-ellipsis whitespace-nowrap overflow-hidden"
        >
          {getLabel(from)}
          {showEmail && (
            <div className="flex flex-col sm:flex-row flex-wrap gap-x-2 text-[11px] font-normal">
              {/* TO Email */}
              <div className="flex items-center gap-1">
                <span className="opacity-50">{getEmail(from)}</span>
              </div>

              {/* CC Block */}
              {Array.isArray(cc) && cc.length > 0 && (
                <div className="flex items-center gap-1">
                  <Typography
                    component="span"
                    fontSize="11px"
                    fontWeight={400}
                    className="opacity-50"
                  >
                    cc:
                  </Typography>

                  {cc.length === 1 ? (
                    <>
                      <ExoAvatar
                        size="16px"
                        picture={
                          getFormattedData(cc[0]?.participant, cc[0]?.type)
                            ?.picture
                        }
                        name={
                          getFormattedData(cc[0]?.participant, cc[0]?.type)
                            ?.label
                        }
                        type="contact"
                      />
                      <Typography
                        component="span"
                        fontSize="11px"
                        className="opacity-50"
                      >
                        {getEmail(cc)}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <AvatarGroup
                        max={2}
                        sx={{
                          '.MuiAvatar-root': {
                            width: '16px',
                            height: '16px',
                            fontSize: '10px',
                            border: '1px solid transparent',
                            cursor: 'pointer',
                          },
                        }}
                        onClick={(e) =>
                          handleOpenNewParticipantsPopper(e, 'cc')
                        }
                      >
                        {cc.map((element, index) => {
                          const data = getFormattedData(
                            element.participant,
                            element.type
                          );
                          return (
                            <ExoAvatar
                              key={index}
                              size="16px"
                              picture={data?.picture}
                              name={data?.label}
                              type="contact"
                            />
                          );
                        })}
                      </AvatarGroup>
                      <Typography
                        component="span"
                        fontSize="11px"
                        className="opacity-50"
                      >
                        {getEmail(cc)}
                      </Typography>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </Typography>
        {subject && (
          <Typography
            textAlign="start"
            fontSize="11px"
            fontWeight={500}
            className="w-full text-ellipsis overflow-hidden whitespace-nowrap"
          >
            {subject}
          </Typography>
        )}
      </Box>
      <Container
        open={Boolean(participantsAnchorEl)}
        anchorEl={participantsAnchorEl}
        sx={{
          '.MuiBackdrop-root': {
            bgcolor: 'transparent',
            background: 'unset',
            backdropFilter: 'unset',
          },
          '.MuiPaper-root': {
            bgcolor: 'transparent',
            background: 'unset',
            backdropFilter: 'unset',
          },
        }}
        disable
      >
        <ClickAwayListener onClickAway={() => setParticipantsAnchorEl(null)}>
          <Box
            className="px-4 py-2 rounded-lg flex flex-col gap-1 backdrop-blur-md"
            sx={{
              background: `linear-gradient(135deg,  ${colors.glassFrom}, ${colors.glassTo})`,
              border: `1px solid ${colors.selected}`,
              maxHeight: 500,
              overflowY: 'auto',
              minWidth: 280,
            }}
          >
            <Typography fontWeight={600} mb={1} className="flex items-center">
              {participantsSource === 'cc'
                ? t('CC Participants')
                : t('Participants')}
            </Typography>

            {(participantsSource === 'from' ? from : cc)?.map(
              (element, index) => {
                const data = getFormattedData(
                  element.participant,
                  element.type
                );
                const email = element.email;

                return (
                  <Box
                    key={index}
                    className="flex items-center gap-2 py-1"
                    sx={{
                      bgcolor: 'transparent',
                      border: `1px solid transparent`,
                      borderRadius: '5px',
                      paddingX: '5px',
                      ':hover': {
                        bgcolor: colors.info + '30',
                        border: `1px solid ${colors.info}80`,
                        cursor: 'pointer',
                        borderColor: colors.info,
                      },
                      borderBottom: `1px solid ${colors.border}40`,
                    }}
                    onClick={() => {
                      if (data?.to) {
                        navigate(data.to);
                        setParticipantsAnchorEl(null);
                      } else {
                        setSelectedSender(element); // store the clicked participant
                        setOpen(true); // open dialog
                      }
                    }}
                  >
                    <ExoAvatar
                      picture={data?.picture}
                      type={'contact'}
                      name={data?.label}
                      icon={
                        data?.isUnknown ? (
                          <AddIcon fontSize="small" />
                        ) : undefined
                      }
                      size="35px"
                      sx={{
                        ':hover': {
                          cursor: 'pointer',
                        },
                      }}
                    />
                    <Box className="flex flex-col">
                      <Typography fontSize={13} fontWeight={500}>
                        {data?.label}
                      </Typography>
                      <Typography fontSize={11} color="textSecondary">
                        {email}
                      </Typography>
                    </Box>
                  </Box>
                );
              }
            )}
          </Box>
        </ClickAwayListener>
      </Container>
      <ExoDialog
        open={open}
        onClose={() => {
          setOpen(false);
          setSelectedSender(null);
        }}
        limitWidth
      >
        <ExoVariants variants={variants} />
      </ExoDialog>
    </Box>
  );
};

export default ECFromStack;
